import { ChevronDown } from 'lucide-react';
import { useId } from 'react';

import { ChonKy } from '../ui/ChonKy.js';
import { KY_NGAY, NHAN_KY_NGAY, quyCuaThangCuaLich, thangHienTai, useKyChung } from './kyChung.js';

/**
 * Ô chọn thời gian của thanh trên — **một** ô cho cả app.
 *
 * Bốn kiểu, do trang khai (xem `kyChung.ts`):
 *
 * - `moc`: tám dạng kỳ như YouTube Studio — dựng bằng `ui/ChonKy`, **cùng**
 *   component với ô ở đầu trang chi tiết kênh.
 * - `ngay`: bản cũ, cửa sổ trượt 7 / 28 / 90 / 365 ngày và toàn thời gian.
 * - `thang`: 12 tháng gần nhất + 4 quý gần nhất.
 * - `thang-don`: 12 tháng gần nhất, **không** quý — trang mà API chỉ nhận
 *   `YYYY-MM` (Doanh thu, Thu nhập của tôi).
 *
 * Là một `<select>` thật chứ không menu tự vẽ: nó có bàn phím, có trình đọc
 * màn hình và trên điện thoại mở bánh xe chọn của hệ điều hành — ba thứ một
 * dropdown tự vẽ phải làm lại từ đầu và thường làm sai.
 */

/** 12 kỳ tháng gần nhất, mới nhất trước. */
function thangGanDay(soKy = 12, homNay = new Date()): string[] {
  return Array.from({ length: soKy }, (_, i) => {
    const d = new Date(Date.UTC(homNay.getUTCFullYear(), homNay.getUTCMonth() - i, 1));
    return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

function nhanThang(ky: string): string {
  const [nam, thang] = ky.split('-');
  return `Tháng ${thang ?? '?'}/${nam ?? '?'}`;
}

export function OChonKyChung() {
  const ky = useKyChung();
  const id = useId();

  if (ky.kieu === null) return null;

  /*
   * Kiểu `moc`: ô chọn 8 dạng + hộp "Tuỳ chỉnh…". Không in khoảng ngày cạnh ô
   * — thanh trên chật, và dòng phụ dưới tiêu đề từng trang đã ghi đúng khoảng
   * đó ("28 ngày qua · 23/09 – 20/10/2026").
   */
  if (ky.kieu === 'moc' && ky.ky) {
    return <ChonKy ky={ky.ky} moc={ky.moc} onChon={ky.dat} />;
  }

  const thang = thangGanDay().map((t) => ({ giaTri: t, nhan: nhanThang(t) }));
  const quy = [...new Set(thangGanDay(12).map(quyCuaThangCuaLich))]
    .slice(0, 4)
    .map((q) => ({ giaTri: q, nhan: `Quý ${q.replace('-Q', '/')}` }));

  const tuyChon =
    ky.kieu === 'ngay'
      ? KY_NGAY.map((k) => ({ giaTri: k as string, nhan: NHAN_KY_NGAY[k] }))
      : ky.kieu === 'thang-don'
        ? thang
        : [...thang, ...quy];

  /*
   * Giá trị lạ trên URL (ví dụ `?ky=2019-03` khi danh sách chỉ có 12 tháng gần
   * nhất) vẫn phải chọn được, nếu không `<select>` sẽ tự nhảy về mục đầu và âm
   * thầm đổi kỳ người dùng đang xem.
   */
  const coSan = tuyChon.some((t) => t.giaTri === ky.giaTri);
  const danhSach = coSan
    ? tuyChon
    : [
        {
          giaTri: ky.giaTri,
          nhan: ky.laQuy ? `Quý ${ky.giaTri.replace('-Q', '/')}` : nhanThang(ky.giaTri),
        },
        ...tuyChon,
      ];

  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        Kỳ số liệu
      </label>
      <select
        id={id}
        value={ky.giaTri}
        onChange={(e) => {
          ky.dat(e.target.value);
        }}
        className="vung-bam w-full cursor-pointer appearance-none rounded-o border border-vien bg-be-mat pr-9 pl-3 text-co-ban font-medium text-chu hover:bg-neutral-nen sm:w-auto"
      >
        {danhSach.map((t) => (
          <option key={t.giaTri} value={t.giaTri}>
            {t.nhan}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-chu-phu"
      />
    </div>
  );
}

/** Kỳ tháng mặc định, dùng khi trang cần một giá trị trước khi URL có `?ky=`. */
export { thangHienTai };
